import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output} from '@angular/core';
import {buildMonthGrid} from './calendar-utils';
import {CalendarDay, CalendarItem, CalendarLegend} from './calendar.model';

type ItemsMap = Record<string, CalendarItem[]>;

@Component({
    selector: 'app-calendar',
    templateUrl: './calendar.component.html',
    styleUrl: './calendar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarComponent implements OnInit, OnDestroy {
    private _monthDate = new Date();
    @Input()
    set monthDate(v: Date) {
        if (!v) {
            return;
        }
        const normalized = new Date(v.getFullYear(), v.getMonth(), 1);
        if (this._monthDate.getTime() !== normalized.getTime()) {
            this._monthDate = normalized;
            this.rebuildGridSyncIfNeeded();
        }
    }

    get monthDate() {
        return this._monthDate;
    }

    private _items: ItemsMap | null = null;
    @Input()
    set items(v: ItemsMap | null) {
        const map = v || null;
        if (this._items === map) {
            return;
        }
        this._items = map;
        this.scheduleRebuild('items');
    }

    get items() {
        return this._items;
    }

    @Input() maxItemsPerCell = 3;
    @Input() weekStartsOn: 0 | 1 = 1;
    @Input() disableNextMonth = false;
    @Input() calendarLegend: CalendarLegend[] = [];

    @Output() monthChange = new EventEmitter<Date[]>();
    @Output() dayClick = new EventEmitter<Date>();
    @Output() itemClick = new EventEmitter<{ date: Date; item: CalendarItem }>();

    weeks: CalendarDay[] = [];

    private gridCache: CalendarDay[] | null = null;
    private gridKey = '';
    private scheduledFor: Set<'month' | 'items' | 'weekStart'> = new Set();

    private frameHandle: number | null = null;
    private usingRAF = true;

    disabledNextButton = false;

    today = new Date();

    constructor(private ngZone: NgZone, private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        const initialGrid = buildMonthGrid(this._monthDate, this.weekStartsOn);
        this.gridCache = initialGrid;
        this.gridKey = this.makeGridKey();
        this.weeks = this.gridCache;
        if (this._items) {
            this.scheduleRebuild('items');
        }

        this.disabledNextButton = this.disableNextMonth;
    }

    ngOnDestroy(): void {
        if (this.frameHandle != null) {
            if (this.usingRAF && typeof cancelAnimationFrame === 'function') {
                cancelAnimationFrame(this.frameHandle);
            } else {
                clearTimeout(this.frameHandle);
            }
            this.frameHandle = null;
        }
    }

    private dateKey(d: Date): string {
        const y = d.getFullYear();
        const m = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    private makeGridKey(): string {
        return `${this._monthDate.getFullYear()}-${this._monthDate.getMonth()}-${this.weekStartsOn}`;
    }

    /**
     * Schedule a batched rebuild. We run expensive computation outside Angular zone
     * and apply changes inside Angular once — this reduces CD churn.
     */
    private scheduleRebuild(trigger: 'month' | 'items' | 'weekStart') {
        this.scheduledFor.add(trigger);
        if (this.frameHandle != null) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            const raf = window.requestAnimationFrame;
            if (typeof raf === 'function') {
                this.usingRAF = true;
                this.frameHandle = raf(() => this.onFrameTick());
            } else {
                this.usingRAF = false;
                this.frameHandle = (window.setTimeout as any)(() => this.onFrameTick(), 0) as number;
            }
        });
    }

    private onFrameTick() {
        this.frameHandle = null;
        this.usingRAF = true;

        const result = this.computeGridAndUpdates();

        this.ngZone.run(() => {
            this.applyGrid(result);
            if (this.scheduledFor.has('month') || this.scheduledFor.has('items')) {
                if (this.weeks.length > 0) {
                    this.monthChange.emit([this.weeks[0].date, this.weeks[this.weeks.length - 1].date]);
                }
            }
            this.scheduledFor.clear();
            this.cdr.markForCheck();
        });
    }

    private rebuildGridSyncIfNeeded() {
        const desiredKey = this.makeGridKey();
        if (!this.gridCache || this.gridKey !== desiredKey) {
            this.gridCache = buildMonthGrid(this._monthDate, this.weekStartsOn);
            this.gridKey = desiredKey;
            this.weeks = this.gridCache;
            this.cdr.markForCheck();
        }
    }

    /**
     * computeGridAndUpdates runs outside Angular — it should not touch change detection.
     * It updates cached cells in-place (fast) and prepares visibleItems/extra.
     */
    private computeGridAndUpdates(): { grid: CalendarDay[]; replacedGrid: boolean } {
        const desiredGridKey = this.makeGridKey();
        let grid: CalendarDay[];

        if (!this.gridCache || this.gridKey !== desiredGridKey) {
            grid = buildMonthGrid(this._monthDate, this.weekStartsOn);
            this.gridCache = grid;
            this.gridKey = desiredGridKey;
        } else {
            grid = this.gridCache!;
        }

        const items = this._items || {};
        const maxItems = this.maxItemsPerCell;

        for (let i = 0; i < grid.length; i++) {
            const cell = grid[i];
            const key = this.dateKey(cell.date);
            const list = items[key] || [];

            const prev = cell.items || [];
            let needsAssign = false;
            if (!prev || prev.length !== list.length) {
                needsAssign = true;
            } else {
                for (let k = 0; k < prev.length; k++) {
                    if (!prev[k] || !list[k] || prev[k].id !== list[k].id) {
                        needsAssign = true;
                        break;
                    }
                }
            }
            cell.items = needsAssign ? list : prev;

            cell.visibleItems = list.length ? list.slice(0, maxItems) : [];
            cell.extra = Math.max(0, list.length - maxItems);
        }

        return {grid: grid.slice(0), replacedGrid: false};
    }

    private applyGrid(_result: { grid: CalendarDay[]; replacedGrid: boolean }) {
        if (this.weeks !== this.gridCache) {
            this.weeks = this.gridCache!;
        } else {
            this.cdr.markForCheck();
        }
    }

    onDayClick(day: CalendarDay) {
        this.dayClick.emit(day.date);
    }

    onItemClick(date: Date, item: CalendarItem, evt: Event) {
        if (evt) {
            evt.stopPropagation();
        }
        this.itemClick.emit({date, item});
    }

    previousMonth(): void {
        this.monthDate = new Date(this._monthDate.getFullYear(), this._monthDate.getMonth() - 1, 1);
        this.scheduleRebuild('month');
        this.updateDisabledNextButton();
    }

    nextMonth(): void {
        this.monthDate = new Date(this._monthDate.getFullYear(), this._monthDate.getMonth() + 1, 1);
        this.scheduleRebuild('month');
        this.updateDisabledNextButton();
    }

    private updateDisabledNextButton(): void {
        if (!this.disableNextMonth) {
            this.disabledNextButton = false;
            return;
        }

        const currentY = this.today.getFullYear();
        const currentM = this.today.getMonth();

        const viewedY = this._monthDate.getFullYear();
        const viewedM = this._monthDate.getMonth();

        this.disabledNextButton = (viewedY === currentY && viewedM === currentM);
    }

}
